// src/frontend_react/src/services/api.js
import axios from 'axios';

function getCentralApiRootUrl() {
  // 1. Priority: Config injected by the Electron main process.
  if (window.electronAppConfig && window.electronAppConfig.centralApiUrl) {
    console.log(
      '[API.js] Detected Electron environment. Using config from electronAppConfig.',
    );
    const urlFromElectron = window.electronAppConfig.centralApiUrl;
    // The functions below append /api, so we return the base URL.
    return urlFromElectron.replace(/\/api$/, '');
  }

  // 2. Fallback: Use an environment variable, primarily for a potential production web deployment.
  if (process.env.REACT_APP_API_BASE_URL) {
    console.log(
      `[API.js] Detected REACT_APP_API_BASE_URL. Using it: ${process.env.REACT_APP_API_BASE_URL}`,
    );
    return process.env.REACT_APP_API_BASE_URL;
  }

  // 3. Final Fallback: A sane default for local development in a standard browser.
  console.log(
    '[API.js] No specific config found. Falling back to local development URL.',
  );
  return 'http://localhost:8000';
}

const CENTRAL_API_ROOT_URL = getCentralApiRootUrl();
console.log(
  '[API.js] Final CENTRAL_API_ROOT_URL being used:',
  CENTRAL_API_ROOT_URL,
);

// --- Core API Handling & Helpers ---

async function handleApiResponse(response) {
  if (!response.ok) {
    let errorData = {};
    try {
      errorData = await response.json();
    } catch (e) {
      errorData.message =
        response.statusText ||
        'An API error occurred and the response was not valid JSON.';
    }
    const errorMessage = String(
      errorData.message || errorData.error || 'An unknown API error occurred.',
    );
    const errorDetails = errorData.details || null;

    const customError = new Error(errorMessage);
    customError.details =
      typeof errorDetails === 'object'
        ? JSON.stringify(errorDetails, null, 2)
        : errorDetails;
    customError.status = response.status;
    console.error(
      `API Error: ${customError.status} - ${customError.message}`,
      customError.details || '',
    );
    throw customError;
  }
  if (response.status === 204) {
    return { success: true, message: 'Operation successful (No Content)' };
  }
  return response.json();
}

async function makeJsonRequest(url, method, data = null) {
  const options = {
    method: method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  };
  if (data !== null) {
    options.body = JSON.stringify(data);
  }
  const response = await fetch(url, options);
  return handleApiResponse(response);
}

async function makeCredentialedGetRequest(url) {
  const options = { method: 'GET', credentials: 'include' };
  const response = await fetch(url, options);
  return handleApiResponse(response);
}

// --- API Function Exports ---

// Auth & Health
export async function loginUser(username, password) {
  return makeJsonRequest(`${CENTRAL_API_ROOT_URL}/api/auth/login`, 'POST', {
    username,
    password,
  });
}
export async function logoutUser() {
  return makeJsonRequest(`${CENTRAL_API_ROOT_URL}/api/auth/logout`, 'POST');
}
export async function getUserStatus() {
  return makeCredentialedGetRequest(`${CENTRAL_API_ROOT_URL}/api/auth/status`);
}
export async function getAppHealth() {
  return makeCredentialedGetRequest(`${CENTRAL_API_ROOT_URL}/api/status/app`);
}
export async function getDbHealth() {
  return makeCredentialedGetRequest(`${CENTRAL_API_ROOT_URL}/api/status/db`);
}

// User Data Operations
export const fetchProjects = () =>
  makeCredentialedGetRequest(`${CENTRAL_API_ROOT_URL}/api/data/projects`);
export const listBatches = (projectId) =>
  makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/batches`,
  );
export const getBatchDetails = (batchId) =>
  makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/batches/${batchId}`,
  );
export const createBatch = (projectId, name) =>
  makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/batches`,
    'POST',
    { name },
  );
export const getProjectStatus = (projectId) =>
  makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/status`,
  );
export const getProjectDataPrepStatus = (projectId) =>
  makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/data-preparation-status`,
  );
export const getLastLinkageInfo = (projectId) =>
  makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/linkage/last-info`,
  );
export const runLinkageForBatch = (projectId, batchId) =>
  makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/batches/${batchId}/trigger-linkage`,
    'POST',
    { project_id: projectId },
  );


export const listEncryptedFiles = (projectId, fileType) =>
  makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/files?type=${fileType}`,
  );
export const decryptFile = (projectId, processedFileId) =>
  makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/decrypt`,
    'POST',
    { processed_file_id: processedFileId },
  );
export async function deleteProcessedFile(projectId, processedFileId) {
  const response = await fetch(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/files/${processedFileId}`,
    { method: 'DELETE', credentials: 'include' },
  );
  return handleApiResponse(response);
}
export const requestHashingKeyForBatch = (projectId, batchId) =>
  makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/batches/${batchId}/client-hashing-key`,
    'POST',
    { project_id: projectId },
  );
export const requestUploadUrl = (projectId, filename, sourceType) =>
  makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/request-upload-url`,
    'POST',
    { filename, source_type: sourceType },
  );
export const registerProcessedFileMetadata = (
  projectId,
  batchId,
  metadataPayload,
) =>
  makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/deidentified-data-upload`,
    'POST',
    { ...metadataPayload, batch_id: batchId },
  );
export const uploadFileDirectly = async (
  uploadUrl,
  fileBlob,
  onUploadProgress,
) => {
  const uploadResponse = await axios.put(uploadUrl, fileBlob, {
    headers: { 'Content-Type': fileBlob.type || 'application/octet-stream' },
    onUploadProgress,
  });
  return uploadResponse.data;
};

export async function downloadLinkageReport(projectId, reportFileId, suggestedFilename = null) {
    const url = `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/files/${reportFileId}/download-report`;
    const response = await fetch(url, { method: 'GET', credentials: 'include' });
    
    if (!response.ok) return handleApiResponse(response);
    
    const contentType = response.headers.get('content-type');
    
    if (contentType?.includes('application/json')) {
        return response.json();
    } else {
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = suggestedFilename; 
      if (!filename && contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch?.[1]) filename = filenameMatch[1];
      }
      if (!filename) filename = `linkage_report_${reportFileId}.txt`;
      return { fileBlob: blob, filename: filename };
    }
}

export async function downloadDecryptedFile(projectId, processedFileId, suggestedFilename = null) {
  const url = `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/decrypted-files/${processedFileId}/download`;
  const response = await fetch(url, { method: 'GET', credentials: 'include' });

  if (!response.ok) return handleApiResponse(response); // Throws error

  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    return response.json();
  } else {
    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let filename = suggestedFilename; 
    if (!filename && contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      if (filenameMatch?.[1]) filename = filenameMatch[1];
    }
    if (!filename) filename = `decrypted_file_${processedFileId}.dat`;
    return { fileBlob: blob, filename: filename };
  }
}

// Admin Operations
export async function getAdminUsers() {
  return makeCredentialedGetRequest(`${CENTRAL_API_ROOT_URL}/api/admin/users`);
}
export async function getAdminUserById(userId) {
  return makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/users/${userId}`,
  );
}
export async function createAdminUser(userData) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/users`,
    'POST',
    userData,
  );
}
export async function updateAdminUser(userId, userData) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/users/${userId}`,
    'PUT',
    userData,
  );
}
export async function getAdminRoles() {
  return makeCredentialedGetRequest(`${CENTRAL_API_ROOT_URL}/api/admin/roles`);
}
export async function getAdminRoleById(roleId) {
  return makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/roles/${roleId}`,
  );
}
export async function createAdminRole(roleData) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/roles`,
    'POST',
    roleData,
  );
}
export async function updateAdminRole(roleId, roleData) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/roles/${roleId}`,
    'PUT',
    roleData,
  );
}
export async function deleteAdminRole(roleId) {
  const response = await fetch(
    `${CENTRAL_API_ROOT_URL}/api/admin/roles/${roleId}`,
    { method: 'DELETE', credentials: 'include' },
  );
  return handleApiResponse(response);
}
export async function getAdminProjects() {
  return makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/projects`,
  );
}
export async function getAdminProjectById(projectId) {
  return makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/projects/${projectId}`,
  );
}
export async function createAdminProject(projectData) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/projects`,
    'POST',
    projectData,
  );
}
export async function updateAdminProject(projectId, projectData) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/projects/${projectId}`,
    'PUT',
    projectData,
  );
}
export async function deleteAdminProject(projectId) {
  const response = await fetch(
    `${CENTRAL_API_ROOT_URL}/api/admin/projects/${projectId}`,
    { method: 'DELETE', credentials: 'include' },
  );
  return handleApiResponse(response);
}
export async function getAdminDataSourceTypes() {
  return makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/datasourcetypes`,
  );
}
export async function getAdminDataSourceTypeById(dsTypeId) {
  return makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/datasourcetypes/${dsTypeId}`,
  );
}
export async function createAdminDataSourceType(dsTypeData) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/datasourcetypes`,
    'POST',
    dsTypeData,
  );
}
export async function updateAdminDataSourceType(dsTypeId, dsTypeData) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/datasourcetypes/${dsTypeId}`,
    'PUT',
    dsTypeData,
  );
}
export async function deleteAdminDataSourceType(dsTypeId) {
  const response = await fetch(
    `${CENTRAL_API_ROOT_URL}/api/admin/datasourcetypes/${dsTypeId}`,
    { method: 'DELETE', credentials: 'include' },
  );
  return handleApiResponse(response);
}
export async function getAdminDashboardStats() {
  return makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/dashboard/stats`,
  );
}
export async function getAdminSecretsMetadata(projectId = null) {
  let url = `${CENTRAL_API_ROOT_URL}/api/admin/secrets`;
  if (projectId) url += `?project_id=${projectId}`;
  return makeCredentialedGetRequest(url);
}
export async function generateAdminSecret(projectId, secretName) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/secrets/generate`,
    'POST',
    { project_id: projectId, name: secretName },
  );
}
export async function rotateAdminSecret(projectId, secretName) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/secrets/rotate`,
    'POST',
    { project_id: projectId, name: secretName },
  );
}
export async function destroyAdminSecret(secretId, projectIdContext) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/secrets/${secretId}`,
    'DELETE',
    { project_id: projectIdContext },
  );
}
export async function generateMockData(options = {}) {
  if (!options.project_id)
    throw new Error('Project ID is required for generating mock data.');
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/generate-mock-data`,
    'POST',
    options,
  );
}
export async function adminInitiateMfaSetup(userId) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/users/${userId}/initiate-mfa-setup`,
    'POST',
  );
}
export async function adminVerifyAndEnableMfa(userId, totpCode) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/admin/users/${userId}/verify-mfa-enable`,
    'POST',
    { totp_code: totpCode },
  );
}
export async function getCentralConfigForLocalApp(projectId) {
  return makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/local-app-config`,
  );
}
export async function getSecureClientConfig(projectId) {
  return makeCredentialedGetRequest(
    `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/secure-client-config`,
  );
}
export async function initiateMfaSetup() {
  return makeJsonRequest(`${CENTRAL_API_ROOT_URL}/api/auth/mfa/setup`, 'POST');
}

export async function verifyMfaLogin(totpCode) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/auth/mfa/verify-login`,
    'POST',
    { totp_code: totpCode },
  );
}

export async function verifyAndEnableMfa(totpCode) {
  return makeJsonRequest(
    `${CENTRAL_API_ROOT_URL}/api/auth/mfa/verify-and-enable`,
    'POST',
    { totp_code: totpCode },
  );
}

export async function downloadRawMockFile(projectId, fileId, suggestedFilename = null) {
  const url = `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/files/${fileId}/download-raw`;
  const response = await fetch(url, { method: 'GET', credentials: 'include' });

  if (!response.ok) return handleApiResponse(response);

  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    return response.json();
  } else {
    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    let filename = suggestedFilename; 
    if (!filename && contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      if (filenameMatch?.[1]) filename = filenameMatch[1];
    }
    if (!filename) filename = `mock_file_${fileId}.csv`;
    return { fileBlob: blob, filename: filename };
  }
}

export async function downloadAllRawMockFilesAsZip(projectId, fileIds) {
  const url = `${CENTRAL_API_ROOT_URL}/api/data/projects/${projectId}/download-batch-raw`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ file_ids: fileIds }),
  });

  if (!response.ok) {
    return handleApiResponse(response);
  }

  const contentDisposition = response.headers.get('content-disposition');
  let filename = `mock_data_${projectId}.zip`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (filenameMatch?.[1]) filename = filenameMatch[1];
  }
  
  const blob = await response.blob();
  return { fileBlob: blob, filename: filename };
}